import React from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { Modal, Panel, PanelGroup, designSystem } from '@yola/ws-ui';
import Breadcrumbs from 'src/js/modules/common/components/breadcrumbs';
import { surfacesKeyMap, colorToSurfaceMap } from '../constants/colors-maps';
import constants from '../constants';

const {
  ActionButton,
  Stack,
  HorizontalStack,
  InfoWithPopover,
  ColorPalette,
  Box,
  DialogHeader,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
  Heading,
  ColorScheme,
  Paragraph,
} = designSystem;

const { DEFAULT_OFFSET_Y, DEFAULT_OFFSET_X, DEFAULT_DIALOG_HEIGHT, DEFAULT_DIALOG_WIDTH } =
  constants;

const ORDERED_COLORS = [
  'ws-light-0',
  'ws-dark-1',
  'ws-primary-0',
  'ws-light-1',
  'ws-dark-0',
  'ws-secondary-0',
];

const CustomPaletteDialog = ({
  offsetX = DEFAULT_OFFSET_X,
  offsetY = DEFAULT_OFFSET_Y,
  captions: {
    title,
    description,
    buttons,
    colors: colorsCaptions,
    popoverInfo,
    titleBreadcrumbs,
    customPaletteLabel,
    colorSchemesLabel,
    resetTooltipLabel,
  },
  advancedColors = {},
  onCancel,
  onSubmit,
  onDragEnd = Function.prototype,
  onColorSelect,
  colorSchemes,
  onColorSchemeReset,
  isDeveloperModeEnabled = false,
  onOverlayClick,
  onDeveloperModeClick,
  onEditPaletteClick,
}) => (
  <Modal
    draggable
    resizable={false}
    fullscreen={bowser.mobile}
    width={DEFAULT_DIALOG_WIDTH}
    height={DEFAULT_DIALOG_HEIGHT}
    offsetX={offsetX}
    offsetY={offsetY}
    dragHandleSelector=".ws-drag-trigger"
    dragCancelSelector=".ws-ui-dialog-header-controls"
    overlay="none"
    handleCancel={onCancel}
    handleSubmit={onSubmit}
    onDragEnd={onDragEnd}
    onOverlayClick={onOverlayClick}
  >
    <PanelGroup height="100%">
      <Panel
        align="middle"
        corners="squared"
        className="ws-drag-trigger"
        style={{ flexShrink: '0' }}
      >
        <DialogHeader paddingBottom="spacing-s">
          <Box>
            <Box paddingBottom="spacing-2-xs">
              <Breadcrumbs breadcrumbs={titleBreadcrumbs} />
            </Box>
            <Box>
              <Stack>
                <Heading type="heading-3">{title}</Heading>
                <Paragraph>{description}</Paragraph>
              </Stack>
            </Box>
          </Box>
        </DialogHeader>
      </Panel>
      <Panel height="100%" scrollable style={{ borderBottom: 'none' }}>
        <Box
          paddingTop="spacing-s"
          paddingBottom="spacing-s"
          paddingLeft="spacing-m"
          paddingRight="spacing-m"
        >
          <Stack>
            <div className="ws-custom-palette-dialog__palette-preview">
              <Box marginBottom="spacing-2-xs">
                <Heading type="heading-6">{customPaletteLabel}</Heading>
              </Box>
              <ColorPalette
                colors={ORDERED_COLORS.map((key) => colorSchemes[key].value)}
                layout="2-row"
                size="large"
              />
            </div>
            <Box marginTop="spacing-3-xs">
              <ActionButton label={buttons.editPalette} onClick={onEditPaletteClick} />
            </Box>
            <Box marginTop="spacing-m" marginBottom="spacing-3-xs">
              <HorizontalStack verticalAlign="center">
                <Heading type="heading-6">{colorSchemesLabel}</Heading>
                <InfoWithPopover content={popoverInfo} placement="top-center" />
              </HorizontalStack>
            </Box>
            <Stack gap="spacing-3-xs">
              {Object.entries(surfacesKeyMap).map(([colorType, colorKey]) => {
                const surfaceKey = colorToSurfaceMap[colorKey];
                const advancedBackgroundColorKey = `wsColor${surfaceKey}Background`;
                const backgroundColor =
                  advancedColors[advancedBackgroundColorKey] || colorSchemes[colorKey].value;

                return (
                  <ColorScheme
                    key={colorType}
                    title={colorsCaptions[colorType]}
                    backgroundColor={backgroundColor}
                    isDarkTheme={colorSchemes[colorKey].isDarkTheme}
                    resetTooltipLabel={resetTooltipLabel}
                    onEditClick={() => onColorSelect({ colorKey })}
                    {...(colorSchemes[colorKey].isResetAvailable && {
                      onResetClick: () => onColorSchemeReset({ colorKey }),
                    })}
                    {...(isDeveloperModeEnabled && {
                      onDesignConfigure: () => onDeveloperModeClick({ colorKey }),
                    })}
                  />
                );
              })}
            </Stack>
          </Stack>
        </Box>
      </Panel>
      <Panel corners="squared" style={{ flexShrink: '0' }}>
        <DialogButtonGroup>
          <DialogButton onClick={onSubmit}>{buttons.submit}</DialogButton>
          <DialogButtonGroupSeparator />
          <DialogButton onClick={onCancel}>{buttons.cancel}</DialogButton>
        </DialogButtonGroup>
      </Panel>
    </PanelGroup>
  </Modal>
);

CustomPaletteDialog.propTypes = {
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onColorSelect: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func,
  captions: PropTypes.shape({
    popoverInfo: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    titleBreadcrumbs: PropTypes.arrayOf(PropTypes.string),
    customPaletteLabel: PropTypes.string,
    colorSchemesLabel: PropTypes.string,
    resetTooltipLabel: PropTypes.string,
    buttons: PropTypes.shape({
      submit: PropTypes.string,
      cancel: PropTypes.string,
      editPalette: PropTypes.string,
    }),
    colors: PropTypes.shape({
      altLight: PropTypes.string,
      light: PropTypes.string,
      dark: PropTypes.string,
      altDark: PropTypes.string,
      primary: PropTypes.string,
      secondary: PropTypes.string,
    }).isRequired,
  }).isRequired,
  advancedColors: PropTypes.shape(),
  colorSchemes: PropTypes.shape().isRequired,
  isDeveloperModeEnabled: PropTypes.bool,
  onOverlayClick: PropTypes.func.isRequired,
  onDeveloperModeClick: PropTypes.func.isRequired,
  onColorSchemeReset: PropTypes.func.isRequired,
  onEditPaletteClick: PropTypes.func.isRequired,
};

export default CustomPaletteDialog;
